import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import { Header } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import {
  FiPercent,
  FiTag,
  FiSearch,
  FiUsers,
  FiCopy,
  FiCheck,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiPlus,
  FiTrash2,
  FiEdit3,
} from "react-icons/fi";
import { FaXmark } from "react-icons/fa6";
import { dateToUnix, unixToDateAndTime } from "../../util";

const inputCls =
  "h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue px-3 transition-all placeholder:text-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed";
const selectCls =
  "h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-none focus:border-blue px-3 transition-all cursor-pointer disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed";

const FormField = ({ label, required, children, hint }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-bold text-gray-700 flex items-center gap-1">
      <span>{label}</span>
      {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
    {hint && <span className="text-[11px] text-gray-500 font-normal">{hint}</span>}
  </div>
);

export const ManagePromoCode = () => {
  const { callApi } = Context();
  const { toast } = useToast();

  const [promoCodes, setPromoCodes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPromo, setSelectedPromo] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterApplicableTo, setFilterApplicableTo] = useState("all");

  useEffect(() => {
    getPromoCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPromoCodes = async () => {
    setLoading(true);
    const result = await callApi(
      "promocode-getAll",
      {
        orderBy: ["createdAt", "desc"],
      },
      { notShowLoading: true }
    );
    setPromoCodes(result || []);
    setLoading(false);
  };

  const toggleStatus = async (promo) => {
    if (!window.confirm(`ท่านต้องการเปลี่ยนสถานะ Promo Code "${promo.code}" ใช่หรือไม่`)) return;
    setLoading(true);
    const newStatus = promo.status === "active" ? "inactive" : "active";
    const result = await callApi("promocode-editPromoCode", {
      ...promo,
      status: newStatus,
    });
    if (result.status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: "อัพเดตสถานะสำเร็จ",
      });
      getPromoCodes();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "อัพเดตสถานะไม่สำเร็จ",
        description: result.toastMessage || result.message,
      });
    }
    setLoading(false);
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast({
      className: "bg-[rgb(59,176,101)]",
      title: `คัดลอกรหัส "${code}" สำเร็จ`,
    });
    setTimeout(() => {
      setCopiedCode((prev) => (prev === code ? "" : prev));
    }, 2000);
  };

  // Dashboard Stats
  const stats = useMemo(() => {
    if (!promoCodes) return { total: 0, active: 0, inactive: 0, totalUsages: 0 };
    const now = Date.now();
    let total = promoCodes.length;
    let active = 0;
    let inactive = 0;
    let totalUsages = 0;

    promoCodes.forEach((p) => {
      const isExpired = p.endDate && p.endDate < now;
      const isLimitReached = p.usageLimit !== null && p.usageCount >= p.usageLimit;
      if (p.status === "active" && !isExpired && !isLimitReached) {
        active += 1;
      } else {
        inactive += 1;
      }
      totalUsages += Number(p.usageCount) || 0;
    });

    return { total, active, inactive, totalUsages };
  }, [promoCodes]);

  // Filtered List
  const filteredPromoCodes = useMemo(() => {
    if (!promoCodes) return [];
    const now = Date.now();
    const query = searchQuery.trim().toLowerCase();

    return promoCodes.filter((promo) => {
      const isExpired = promo.endDate && promo.endDate < now;
      const isLimitReached = promo.usageLimit !== null && promo.usageCount >= promo.usageLimit;

      // Status filter
      if (filterStatus === "active") {
        if (promo.status !== "active" || isExpired || isLimitReached) return false;
      } else if (filterStatus === "inactive") {
        if (promo.status === "active") return false;
      } else if (filterStatus === "expired") {
        if (!isExpired) return false;
      }

      // Type filter
      if (filterType !== "all" && promo.type !== filterType) {
        return false;
      }

      // ApplicableTo filter
      if (filterApplicableTo !== "all") {
        const applicable = promo.applicableTo || "lottery";
        if (applicable !== filterApplicableTo && applicable !== "all") return false;
      }

      // Search query
      if (query) {
        const matchCode = promo.code?.toLowerCase().includes(query);
        const matchReason = promo.reason?.toLowerCase().includes(query);
        const matchPhone = promo.phones?.some((ph) => ph.includes(query));
        if (!matchCode && !matchReason && !matchPhone) return false;
      }

      return true;
    });
  }, [promoCodes, searchQuery, filterStatus, filterType, filterApplicableTo]);

  return (
    <div className="h-fit w-full min-w-0 max-w-[1600px] mx-auto pb-10 px-2 sm:px-4" id="main">
      <Header
        header="Promo Codes"
        subHeader="จัดการและตรวจสอบรหัสโปรโมชั่นของลูกค้า"
        icon={<FiPercent className="text-blue text-2xl" />}
      />

      {/* Quick Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500">รหัสทั้งหมด</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-blue/10 text-blue flex items-center justify-center text-xl">
            <FiTag />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-emerald-600">เปิดใช้งาน (Active)</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{stats.active}</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl">
            <FiCheckCircle />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-rose-600">ปิดใช้งาน / หมดอายุ</p>
            <p className="text-2xl font-bold text-rose-700 mt-1">{stats.inactive}</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center text-xl">
            <FiXCircle />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-navy">จำนวนครั้งที่ถูกใช้</p>
            <p className="text-2xl font-bold text-navy mt-1">{stats.totalUsages.toLocaleString()}</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-navy/10 text-navy flex items-center justify-center text-xl">
            <FiUsers />
          </div>
        </div>
      </div>

      {/* Form Drawer/Card */}
      {selectedPromo && (
        <SelectedPromoForm
          selectedPromo={selectedPromo}
          setSelectedPromo={setSelectedPromo}
          getPromoCodes={getPromoCodes}
        />
      )}

      {/* Main Table Card */}
      <div className="mt-5 bg-white rounded-md shadow-md border border-gray-200 overflow-hidden">
        {/* Table Top Controls & Search Bar */}
        <div className="p-4 border-b border-gray-200 bg-gray-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-gray-800">รายการ Promo Codes</h2>
            <span className="px-2.5 py-0.5 text-xs font-bold bg-gray-200 text-gray-700 rounded-md">
              {filteredPromoCodes.length} รายการ
            </span>
          </div>

          <button
            className="py-2.5 px-4 text-sm font-bold rounded-md bg-blue hover:bg-blue/90 text-white shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer self-end sm:self-auto shrink-0"
            onClick={() => {
              setSelectedPromo({});
              document.getElementById("main")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            <FiPlus className="text-base" />
            <span>สร้าง Promo Code ใหม่</span>
          </button>
        </div>

        {/* Filter Bar */}
        <div className="p-4 border-b border-gray-200 bg-white grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          <div className="relative flex items-center">
            <FiSearch className="absolute left-3 text-gray-400 text-sm" />
            <input
              type="text"
              className={`${inputCls} pl-9`}
              placeholder="ค้นหารหัส, เหตุผล, เบอร์..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div>
            <select
              className={selectCls}
              value={filterApplicableTo}
              onChange={(e) => setFilterApplicableTo(e.target.value)}
            >
              <option value="all">ทั้งหมด</option>
              <option value="lottery">คำสั่งซื้อสลาก</option>
              <option value="product">คำสั่งซื้อสินค้า</option>
            </select>
          </div>

          <div>
            <select
              className={selectCls}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">สถานะทั้งหมด</option>
              <option value="active">เปิดใช้งาน (Active)</option>
              <option value="inactive">ปิดใช้งาน (Inactive)</option>
              <option value="expired">หมดอายุแล้ว (Expired)</option>
            </select>
          </div>

          <div>
            <select
              className={selectCls}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">ทุกประเภทสิทธิประโยชน์</option>
              <option value="discount">ส่วนลดบาท (Discount)</option>
              <option value="voucher">ตั๋วสลากฟรี (Voucher)</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="h-10 px-4 w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-md border border-gray-300 shadow-xs transition-all cursor-pointer"
              onClick={() => {
                setSearchQuery("");
                setFilterApplicableTo("all");
                setFilterStatus("all");
                setFilterType("all");
              }}
            >
              <FiRefreshCw className="text-xs" />
              <span>ล้างตัวกรอง</span>
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="w-full overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-100/90 border-b border-gray-200">
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">ลำดับ</TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">รหัส (Code)</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">การใช้งาน</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">ประเภท</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">สิทธิประโยชน์</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">ขั้นต่ำ</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">ระยะเวลาการใช้สิทธิ์</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">โควตาที่ใช้ไป</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">จำกัด/คน</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">สถานะ</TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">จัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && !promoCodes ? (
                <TableRow className="h-[260px]">
                  <TableCell colSpan="11">
                    <div className="w-full h-56 flex flex-col justify-center items-center gap-3 text-gray-500">
                      <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm font-semibold">กำลังโหลดข้อมูล Promo Codes...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredPromoCodes.length === 0 ? (
                <TableRow className="h-[220px]">
                  <TableCell colSpan="11" className="h-48 font-semibold text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <FiTag className="text-3xl text-gray-300" />
                      <p>ไม่พบข้อมูล Promo Code ตามเงื่อนไขที่เลือก</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPromoCodes.map((promo, index) => {
                  const isExpired = promo.endDate && promo.endDate < Date.now();
                  const isLimitReached = promo.usageLimit !== null && promo.usageCount >= promo.usageLimit;
                  const isCopied = copiedCode === promo.code;

                  return (
                    <TableRow key={promo.code} className="hover:bg-gray-50/80 transition-colors border-b border-gray-100">
                      <TableCell className="text-center font-semibold text-gray-500 text-xs">
                        {index + 1}
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-slate-800 text-sm tracking-wide bg-slate-100 px-2 py-1 rounded-md border border-slate-200">
                            {promo.code}
                          </span>
                          <button
                            type="button"
                            title="คัดลอกรหัส"
                            onClick={() => handleCopyCode(promo.code)}
                            className="p-1 text-gray-400 hover:text-blue hover:bg-blue/10 rounded-md transition-all cursor-pointer"
                          >
                            {isCopied ? <FiCheck className="text-emerald-600 text-sm" /> : <FiCopy className="text-sm" />}
                          </button>
                        </div>
                        {promo.reason && (
                          <p className="text-xs text-gray-500 truncate max-w-[200px] mt-1" title={promo.reason}>
                            {promo.reason}
                          </p>
                        )}
                        {promo.phones && promo.phones.length > 0 && (
                          <div className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 font-semibold px-2 py-0.5 rounded-md mt-1">
                            <span>เฉพาะ {promo.phones.length} เบอร์</span>
                          </div>
                        )}
                      </TableCell>

                      <TableCell className="text-center whitespace-nowrap">
                        {(!promo.applicableTo || promo.applicableTo === "lottery") && (
                          <span className="text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-md text-xs font-bold">
                            ลอตเตอรี่
                          </span>
                        )}
                        {promo.applicableTo === "product" && (
                          <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-md text-xs font-bold">
                            สินค้า
                          </span>
                        )}
                        {promo.applicableTo === "all" && (
                          <span className="text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-md text-xs font-bold">
                            ลอตเตอรี่ + สินค้า
                          </span>
                        )}
                      </TableCell>

                      <TableCell className="text-center whitespace-nowrap">
                        {promo.type === "discount" ? (
                          <span className="text-blue bg-blue/10 border border-blue/20 px-2.5 py-1 rounded-md text-xs font-bold">
                            Discount
                          </span>
                        ) : (
                          <span className="text-purple-700 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-md text-xs font-bold">
                            Voucher
                          </span>
                        )}
                      </TableCell>

                      <TableCell className="text-center whitespace-nowrap font-bold text-gray-900 text-sm">
                        {promo.type === "discount" ? (
                          <span className="text-blue font-mono font-bold">{promo.discount?.toLocaleString()} บาท</span>
                        ) : (
                          <span className="text-purple-700 font-mono font-bold">ฟรี {promo.quantity} ใบ</span>
                        )}
                      </TableCell>

                      <TableCell className="text-center whitespace-nowrap text-xs text-gray-600 font-medium">
                        {promo.minimum > 1 && <span className="font-semibold text-gray-800">ขั้นต่ำ {promo.minimum} ใบ</span>}
                        {promo.minimum > 1 && promo.minOrderValue > 0 && <span className="mx-1 text-gray-400">/</span>}
                        {promo.minOrderValue > 0 && <span>ยอด {promo.minOrderValue?.toLocaleString()} บ.</span>}
                        {promo.minimum <= 1 && promo.minOrderValue <= 0 && <span className="text-gray-400">-</span>}
                      </TableCell>

                      <TableCell className="text-center whitespace-nowrap text-xs leading-relaxed">
                        <div className="text-gray-600 font-medium">
                          {new Date(promo.startDate).toLocaleDateString("th-TH", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        <div className="text-gray-400 font-semibold text-[10px]">ถึง</div>
                        <div className={isExpired ? "text-rose-600 font-bold" : "text-gray-600 font-medium"}>
                          {new Date(promo.endDate).toLocaleDateString("th-TH", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        {isExpired && (
                          <span className="inline-block mt-0.5 text-[10px] font-bold text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.2 rounded-md">
                            หมดอายุ
                          </span>
                        )}
                      </TableCell>

                      <TableCell className="text-center whitespace-nowrap font-mono text-sm">
                        <span className={isLimitReached ? "text-rose-600 font-bold" : "text-gray-800 font-bold"}>
                          {promo.usageCount}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {promo.usageLimit !== null ? ` / ${promo.usageLimit}` : " / ∞"}
                        </span>
                        {isLimitReached && (
                          <div className="text-[10px] text-rose-600 font-semibold mt-0.5">ครบโควตา</div>
                        )}
                      </TableCell>

                      <TableCell className="text-center whitespace-nowrap text-xs text-gray-700 font-semibold">
                        {promo.userLimit || 1} ครั้ง
                      </TableCell>

                      <TableCell className="text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => toggleStatus(promo)}
                          className={`min-w-[80px] px-2.5 py-1 text-xs font-bold rounded-md transition-all cursor-pointer ${promo.status === "active"
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
                            : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                            }`}
                        >
                          {promo.status === "active" ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                        </button>
                      </TableCell>

                      <TableCell className="text-center whitespace-nowrap">
                        <button
                          type="button"
                          className="h-8 px-3 inline-flex items-center justify-center gap-1.5 bg-blue hover:bg-blue/90 text-white rounded-md text-xs font-bold shadow-xs transition-all cursor-pointer"
                          onClick={() => {
                            setSelectedPromo(promo);
                            document.getElementById("promo-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                        >
                          <FiEdit3 className="text-xs" />
                          <span>แก้ไข</span>
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const normalizePhone = (p) => {
  if (!p) return "";
  let clean = p.replace(/\D/g, "");
  if (clean.startsWith("66")) {
    clean = "0" + clean.slice(2);
  }
  return clean;
};

const parsePhoneString = (raw) => {
  if (!raw) return [];
  return raw
    .split(/[\s,;\n\r\t]+/)
    .map(normalizePhone)
    .filter((p) => p.length >= 9 && p.length <= 10);
};

const PhoneWhitelistManager = ({ phones = [], onChange }) => {
  const [inputVal, setInputVal] = useState("");
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchText, setBatchText] = useState("");
  const { toast } = useToast();

  const handleAddPhones = (rawText) => {
    const parsed = parsePhoneString(rawText);
    if (parsed.length === 0) {
      toast({ className: "bg-[#c15b78]", title: "ไม่พบเบอร์โทรศัพท์ที่ถูกต้อง (9-10 หลัก)" });
      return;
    }
    const merged = Array.from(new Set([...phones, ...parsed]));
    const addedCount = merged.length - phones.length;
    onChange(merged);
    setInputVal("");
    setBatchText("");
    setShowBatchModal(false);
    toast({
      className: "bg-[rgb(59,176,101)]",
      title: `เพิ่มเบอร์สำเร็จ ${addedCount} เบอร์ (ซ้ำ/ไม่เพิ่ม ${parsed.length - addedCount} เบอร์)`,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (inputVal.trim()) {
        handleAddPhones(inputVal);
      }
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData?.getData("text") || "";
    if (pasteData.includes("\n") || pasteData.includes(",") || pasteData.includes(" ") || pasteData.includes("\t")) {
      e.preventDefault();
      handleAddPhones(pasteData);
    }
  };

  const handleRemove = (indexToRemove) => {
    const next = phones.filter((_, idx) => idx !== indexToRemove);
    onChange(next);
  };

  const handleClearAll = () => {
    if (!window.confirm("ท่านต้องการล้างเบอร์ Whitelist ทั้งหมดเพื่อเปิดให้ใช้ได้ทุกเบอร์ ใช่หรือไม่?")) return;
    onChange([]);
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-4 flex flex-col gap-3">
      {/* Header & Badges */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 pb-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-800">
            จำกัดเฉพาะเบอร์โทรศัพท์ (Whitelist)
          </span>
          {phones.length === 0 ? (
            <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
              ✓ ใช้ได้ทุกเบอร์ (Public)
            </span>
          ) : (
            <span className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
              จำกัดเฉพาะ {phones.length} เบอร์
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowBatchModal((prev) => !prev)}
            className="text-xs font-semibold text-blue hover:text-blue/80 hover:underline cursor-pointer flex items-center gap-1"
          >
            {showBatchModal ? "✕ ปิดกล่องวางจาก Excel" : "📋 วางหลายเบอร์จาก Excel"}
          </button>
          {phones.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs font-semibold text-rose-600 hover:text-rose-700 hover:underline cursor-pointer"
            >
              ล้างทั้งหมด
            </button>
          )}
        </div>
      </div>

      {/* Batch Paste Box (Expandable) */}
      {showBatchModal && (
        <div className="bg-white border border-blue/20 rounded-md p-3 flex flex-col gap-2 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-blue">
              คัดลอกเบอร์จาก Excel / Google Sheets มาวางที่นี่ได้ทันที:
            </span>
            <span className="text-[11px] text-slate-400">รองรับ Newline, Space, Comma, Tab</span>
          </div>
          <textarea
            className="w-full h-24 p-2 text-xs font-mono border border-slate-200 rounded-md focus:outline-none focus:border-blue placeholder:text-slate-400"
            placeholder={"0812345678\n0898765432\n0861112233..."}
            value={batchText}
            onChange={(e) => setBatchText(e.target.value)}
          />
          <div className="flex justify-between items-center">
            <span className="text-[11px] text-slate-500 font-medium">
              {batchText.trim()
                ? `ตรวจพบประมาณ ${parsePhoneString(batchText).length} เบอร์ที่ถูกต้อง`
                : "วางข้อความเพื่อเริ่มนำเข้า"}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowBatchModal(false)}
                className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                disabled={!batchText.trim()}
                onClick={() => handleAddPhones(batchText)}
                className="px-3 py-1.5 text-xs font-bold text-white bg-blue hover:bg-blue/90 disabled:opacity-50 rounded-md cursor-pointer"
              >
                นำเข้าเบอร์โทรศัพท์
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            className={`${inputCls} font-mono`}
            placeholder="พิมพ์เบอร์แล้วกด Enter หรือวางเบอร์โทรที่นี่ (เช่น 0812345678)..."
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        </div>
        <button
          type="button"
          disabled={!inputVal.trim()}
          onClick={() => handleAddPhones(inputVal)}
          className="px-4 text-xs font-bold bg-slate-800 hover:bg-slate-900 disabled:opacity-40 text-white rounded-md transition-all cursor-pointer shrink-0"
        >
          เพิ่มเบอร์
        </button>
      </div>

      {/* Chips Area */}
      {phones.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto p-2.5 bg-white rounded-md border border-slate-200">
          {phones.map((phone, idx) => (
            <span
              key={`${phone}_${idx}`}
              className="inline-flex items-center gap-1.5 font-mono text-xs font-bold bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-md shadow-2xs"
            >
              <span>{phone}</span>
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="text-amber-700 hover:text-rose-600 transition-colors cursor-pointer"
                title={`ลบ ${phone}`}
              >
                <FaXmark className="text-xs" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400 font-medium">
          💡 หากไม่ระบุเบอร์โทรศัพท์ ลูกค้าทุกคนในระบบจะสามารถใช้รหัสโปรโมชั่นนี้ได้ตามเงื่อนไขทั่วไป
        </p>
      )}
    </div>
  );
};

const SelectedPromoForm = ({ selectedPromo, setSelectedPromo, getPromoCodes }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [form, setForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const isEditMode = !!selectedPromo.code;

  useEffect(() => {
    if (isEditMode) {
      setForm({
        ...selectedPromo,
        applicableTo: selectedPromo.applicableTo || "lottery",
        phones: Array.isArray(selectedPromo.phones) ? [...selectedPromo.phones] : [],
      });
    } else {
      setForm({
        code: "",
        applicableTo: "lottery",
        type: "discount",
        discount: 0,
        quantity: 0,
        minimum: 1,
        minOrderValue: 0,
        startDate: Date.now(),
        endDate: Date.now() + 30 * 24 * 60 * 60 * 1000,
        usageLimit: "",
        userLimit: 1,
        status: "active",
        phones: [],
        reason: "",
      });
    }
  }, [selectedPromo, isEditMode]);

  const handleChange = (key, val) => {
    setForm((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const handleSave = async () => {
    if (!form.code || !form.code.trim()) {
      toast({ className: "bg-[#c15b78]", title: "กรุณาระบุรหัส Promo Code" });
      return;
    }
    if (form.applicableTo === "product" && form.type === "voucher") {
      toast({ className: "bg-[#c15b78]", title: "โค้ดสินค้าต้องเป็นประเภทส่วนลดเงินบาทเท่านั้น" });
      return;
    }
    if (form.type === "discount" && Number(form.discount) <= 0) {
      toast({ className: "bg-[#c15b78]", title: "กรุณาระบุจำนวนส่วนลดมากกว่า 0 บาท" });
      return;
    }
    if (form.type === "voucher" && Number(form.quantity) <= 0) {
      toast({ className: "bg-[#c15b78]", title: "กรุณาระบุจำนวนสลากฟรีมากกว่า 0 ใบ" });
      return;
    }
    if (!form.startDate || !form.endDate) {
      toast({ className: "bg-[#c15b78]", title: "กรุณาระบุระยะเวลาเริ่มต้นและหมดอายุ" });
      return;
    }
    if (form.startDate >= form.endDate) {
      toast({ className: "bg-[#c15b78]", title: "เวลาเริ่มต้นต้องมาก่อนเวลาหมดอายุ" });
      return;
    }

    setLoading(true);
    const apiName = isEditMode ? "promocode-editPromoCode" : "promocode-createPromoCode";
    const payload = {
      ...form,
      code: form.code.trim().toUpperCase(),
      phones: Array.isArray(form.phones) ? form.phones : [],
    };

    const result = await callApi(apiName, payload);
    if (result.status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: isEditMode ? "แก้ไขข้อมูลสำเร็จ" : "สร้าง Promo Code สำเร็จ",
      });
      setSelectedPromo(false);
      getPromoCodes();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: isEditMode ? "แก้ไขข้อมูลไม่สำเร็จ" : "สร้าง Promo Code ไม่สำเร็จ",
        description: result.toastMessage || result.message,
      });
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`ท่านต้องการลบ Promo Code "${form.code}" ใช่หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้`)) return;
    setLoading(true);
    const result = await callApi("promocode-deletePromoCode", { code: form.code });
    if (result.status) {
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: "ลบ Promo Code สำเร็จ",
      });
      setSelectedPromo(false);
      getPromoCodes();
    } else {
      toast({
        className: "bg-[#c15b78]",
        title: "ลบ Promo Code ไม่สำเร็จ",
        description: result.toastMessage || result.message,
      });
    }
    setLoading(false);
  };

  if (!form) return null;

  return (
    <div id="promo-form" className="relative bg-white w-full rounded-md shadow-md mt-5 border border-gray-200 overflow-hidden">
      {loading && (
        <div className="z-50 bg-white/80 backdrop-blur-xs absolute inset-0 flex flex-col justify-center items-center gap-2">
          <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
          <p className="text-xs font-semibold text-gray-600">กำลังประมวลผลข้อมูล...</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center px-6 py-4 border-b border-gray-200 justify-between bg-gray-50/70">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-blue/10 text-blue flex items-center justify-center font-bold">
            <FiTag />
          </div>
          <div>
            <h3 className="font-bold text-base text-gray-800">
              {isEditMode ? `แก้ไข Promo Code: ${form.code}` : "สร้าง Promo Code ใหม่"}
            </h3>
            <p className="text-xs text-gray-500 font-medium">กำหนดสิทธิประโยชน์ เงื่อนไข และระยะเวลาการใช้งาน</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setSelectedPromo(false)}
          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 rounded-md transition-all cursor-pointer"
        >
          <FaXmark className="text-lg" />
        </button>
      </div>

      {/* Live Preview Banner */}
      <div className="px-6 py-3 bg-blue/5 border-b border-blue/10 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-600">ตัวอย่างการแสดงผล:</span>
          <span className="font-mono font-bold text-sm bg-white px-2.5 py-0.5 rounded-md border border-blue/20 text-blue">
            {form.code || "SAMPLECODE"}
          </span>
          <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-700">
            {form.applicableTo === "product"
              ? "ใช้กับสินค้าทั่วไป"
              : form.applicableTo === "all"
                ? "ใช้กับลอตเตอรี่ + สินค้า"
                : "ใช้กับลอตเตอรี่"}
          </span>
          <span className="text-xs font-bold text-gray-800">
            {form.type === "discount"
              ? `ส่วนลด ${Number(form.discount || 0).toLocaleString()} บาท`
              : `แจกฟรีสลาก ${Number(form.quantity || 0)} ใบ`}
          </span>
        </div>
        <div className="text-xs text-gray-500 font-medium">
          {form.minimum > 1
            ? `ขั้นต่ำ ${form.minimum} ${form.applicableTo === "product" ? "ชิ้น" : "ใบ"}`
            : "ไม่จำกัดขั้นต่ำ"}
          {form.minOrderValue > 0 ? ` • ยอดขั้นต่ำ ${Number(form.minOrderValue).toLocaleString()} บ.` : ""}
          {` • จำกัด ${form.userLimit || 1} ครั้ง/คน`}
          {form.phones?.length > 0 ? ` • จำกัดเฉพาะ ${form.phones.length} เบอร์` : ""}
        </div>
      </div>

      {/* Form Fields Grid */}
      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        <FormField label="รหัสโปรโมชั่น (Code)" required hint="ตัวพิมพ์ใหญ่ภาษาอังกฤษหรือตัวเลข">
          <input
            className={`${inputCls} font-mono uppercase`}
            type="text"
            value={form.code}
            disabled={isEditMode}
            onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
            placeholder="เช่น WELCOME10"
          />
        </FormField>

        <FormField label="ใช้กับคำสั่งซิ้อ" required hint="กำหนดว่าโค้ดนี้ใช้กับลอตเตอรี่ สินค้า หรือทั้งหมด">
          <select
            className={selectCls}
            value={form.applicableTo || "lottery"}
            onChange={(e) => {
              const newApp = e.target.value;
              handleChange("applicableTo", newApp);
              if (newApp === "product" && form.type === "voucher") {
                handleChange("type", "discount");
              }
            }}
          >
            <option value="all">ทั้งหมด</option>
            <option value="lottery">คำสั่งซื้อสลาก</option>
            <option value="product">คำสั่งซื้อสินค้า</option>
          </select>
        </FormField>

        <FormField label="ประเภทโปรโมชั่น" required>
          <select
            className={selectCls}
            value={form.type}
            onChange={(e) => handleChange("type", e.target.value)}
          >
            <option value="discount">ส่วนลดบาท (Discount)</option>
            {form.applicableTo !== "product" && (
              <option value="voucher">ตั๋วสลากฟรี (Voucher)</option>
            )}
          </select>
        </FormField>

        <FormField label="สถานะการเปิดใช้งาน" required>
          <select
            className={selectCls}
            value={form.status}
            onChange={(e) => handleChange("status", e.target.value)}
          >
            <option value="active">เปิดใช้งาน (Active)</option>
            <option value="inactive">ปิดใช้งาน (Inactive)</option>
          </select>
        </FormField>

        {form.type === "discount" ? (
          <FormField label="มูลค่าส่วนลด (บาท)" required hint="จำนวนเงินที่จะหักลบออกจากยอดชำระ">
            <input
              className={inputCls}
              type="number"
              min={0}
              value={form.discount}
              onChange={(e) => handleChange("discount", Number(e.target.value))}
              placeholder="เช่น 50"
            />
          </FormField>
        ) : (
          <FormField label="จำนวนสลากฟรี (ใบ)" required hint="จำนวนใบที่จะตัดเป็นฟรีอัตโนมัติ">
            <input
              className={inputCls}
              type="number"
              min={1}
              value={form.quantity}
              onChange={(e) => handleChange("quantity", Number(e.target.value))}
              placeholder="เช่น 1"
            />
          </FormField>
        )}

        <FormField
          label={
            form.applicableTo === "product"
              ? "จำนวนสินค้าขั้นต่ำ (ชิ้น)"
              : form.applicableTo === "all"
                ? "จำนวนขั้นต่ำในตะกร้า (ชิ้น/ใบ)"
                : "สลากขั้นต่ำในตะกร้า (ใบ)"
          }
          hint="ต้องมีสินค้าหรือสลากอย่างน้อยตามจำนวนนี้"
        >
          <input
            className={inputCls}
            type="number"
            min={1}
            value={form.minimum}
            onChange={(e) => handleChange("minimum", Number(e.target.value))}
            placeholder="1"
          />
        </FormField>

        <FormField label="ยอดสั่งซื้อขั้นต่ำ (บาท)" hint="ยอดเงินรวมในตะกร้าขั้นต่ำ">
          <input
            className={inputCls}
            type="number"
            min={0}
            value={form.minOrderValue}
            onChange={(e) => handleChange("minOrderValue", Number(e.target.value))}
            placeholder="0"
          />
        </FormField>

        <FormField label="เวลาเริ่มต้นการใช้สิทธิ์" required>
          <input
            className={inputCls}
            type="datetime-local"
            value={unixToDateAndTime(form.startDate + 7 * 3600000)}
            onChange={(e) => handleChange("startDate", dateToUnix(e.target.value))}
          />
        </FormField>

        <FormField label="เวลาหมดอายุการใช้สิทธิ์" required>
          <input
            className={inputCls}
            type="datetime-local"
            value={unixToDateAndTime(form.endDate + 7 * 3600000)}
            onChange={(e) => handleChange("endDate", dateToUnix(e.target.value))}
          />
        </FormField>

        <FormField label="โควตาสิทธิ์ใช้งานรวมทั้งหมด (Global Limit)" hint="เว้นว่างไว้หากไม่จำกัดสิทธิ์รวม">
          <input
            className={inputCls}
            type="number"
            min={1}
            value={form.usageLimit || ""}
            onChange={(e) => handleChange("usageLimit", e.target.value ? Number(e.target.value) : "")}
            placeholder="ไม่จำกัด (∞)"
          />
        </FormField>

        <FormField label="จำกัดการใช้สิทธิ์ต่อคน (User Limit)" required hint="จำนวนครั้งสูงสุดที่แต่ละคนหรือเบอร์ใช้ได้">
          <input
            className={inputCls}
            type="number"
            min={1}
            value={form.userLimit || ""}
            onChange={(e) => handleChange("userLimit", Number(e.target.value))}
            placeholder="1"
          />
        </FormField>

        <FormField label="เหตุผล / แคมเปญอ้างอิง" hint="สำหรับบันทึกประวัติภายใน">
          <input
            className={inputCls}
            type="text"
            value={form.reason || ""}
            onChange={(e) => handleChange("reason", e.target.value)}
            placeholder="เช่น แจกลูกค้าใหม่เดือนนี้"
          />
        </FormField>

        <div className="md:col-span-3">
          <PhoneWhitelistManager
            phones={form.phones || []}
            onChange={(newPhones) => handleChange("phones", newPhones)}
          />
        </div>
      </div>

      {/* Footer Controls */}
      <div className="flex flex-wrap justify-between items-center bg-gray-50 px-6 py-4 border-t border-gray-200 gap-3">
        <div>
          {isEditMode && (
            <button
              type="button"
              className="py-2.5 px-4 text-sm font-bold rounded-md text-rose-600 bg-rose-50 border border-rose-200 hover:bg-rose-100 transition-all flex items-center gap-1.5 cursor-pointer"
              onClick={handleDelete}
            >
              <FiTrash2 className="text-base" />
              <span>ลบ Promo Code</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            className="py-2.5 px-4 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-all cursor-pointer"
            onClick={() => setSelectedPromo(false)}
          >
            ยกเลิก
          </button>
          <button
            type="button"
            className="py-2.5 px-6 text-sm font-bold rounded-md bg-blue hover:bg-blue/90 text-white shadow-xs transition-all cursor-pointer"
            onClick={handleSave}
          >
            {isEditMode ? "บันทึกการแก้ไข" : "สร้าง Promo Code"}
          </button>
        </div>
      </div>
    </div>
  );
};
